import { set, isEqual, union } from 'lodash'
import { observable, runInAction, toJS } from 'mobx'

import { Data } from './model'

class Draft<T extends Data> {
  private _originalData: T
  private _data: T
  onChange?: (data: T) => void

  constructor(data: T) {
    this._originalData = data
    this._data = observable.object({ ...data })
  }

  get data(): T {
    return this._data
  }

  get hasChanges() {
    return !isEqual(toJS(this.data), this._originalData)
  }

  get changedData() {
    return union(Object.keys(this._originalData), Object.keys(this._data)).reduce(
      (acc, key) => ({
        ...acc,
        ...(this._data[key as keyof T] !== this._originalData[key as keyof T]
          ? { [key as keyof T]: this._data[key as keyof T] }
          : undefined),
      }),
      {},
    ) as Partial<T>
  }

  reset = (key?: keyof T) => {
    runInAction(() => {
      if (key) {
        this._data[key] = this._originalData[key]
      } else {
        this._data = this._originalData
      }
      this.onChange?.(this._data)
    })
  }

  settle = (key?: keyof T) => {
    if (key) {
      this._originalData[key] = this._data[key]
    } else {
      this._originalData = toJS(this._data)
    }
  }

  update = (key: keyof T, value: unknown) => {
    runInAction(() => {
      set(this._data, key, value)
    })
    this.onChange?.(this._data)
  }
  remove = (key: keyof T) => {
    runInAction(() => {
      delete this._data[key]
    })
    this.onChange?.(this._data)
  }
}

export default Draft
